import {
  FaMapMarkerAlt,
  FaUmbrellaBeach,
  FaRegClock,
  FaPlane,
  FaStar,
  FaCamera,
  FaBinoculars,
  FaBed,
  FaUtensils
} from "react-icons/fa";
import { IoMdArrowDropright } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageStoneTown from "../../assets/banner3.jpg";
import imageNungwi from "../../assets/vultures.JPG";
import imageActivities from "../../assets/banner6.jpg";
import imageHotel from "../../assets/banner2.jpg";

/* ---------------- HERO DATA ---------------- */

const hero = {
  kicker: "9 Days Tanzania Photographic Safari",
  title: "Photographic Safari",
  subtitle: "Ngorongoro • Serengeti cultural and wildlife photography",
  backgroundImage: imageNungwi,
  meta: ["Wildlife Photography", "Ngorongoro Crater", "Serengeti Migration"]
};

/* ---------------- OVERVIEW ---------------- */

const overview =
  "A specialized Tanzania photographic safari designed for photographers seeking dramatic wildlife, landscapes, and cultural encounters. The journey covers Ngorongoro Crater and the Serengeti, offering exceptional opportunities to capture Africa’s iconic wildlife and breathtaking scenery.";

/* ---------------- HIGHLIGHTS ---------------- */

const highlights = [
  {
    icon: FaCamera,
    title: "Wildlife Photography",
    description:
      "Capture lions, elephants, rhinos, and migrating herds across Africa’s most iconic wildlife landscapes.",
    color: "#f59e0b"
  },
  {
    icon: FaBinoculars,
    title: "Expert Game Drives",
    description:
      "Guided drives designed specifically for photography, with flexible timing and positioning.",
    color: "#059669"
  },
  {
    icon: FaMapMarkerAlt,
    title: "Iconic Locations",
    description:
      "Explore Ngorongoro Crater and Serengeti National Park — legendary safari destinations.",
    color: "#7c3aed"
  },
  {
    icon: FaBed,
    title: "Comfortable Lodges",
    description:
      "Stay in hand-picked safari lodges and camps located close to wildlife hotspots.",
    color: "#d97706"
  }
];

/* ---------------- DETAILS ---------------- */

const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Destination",
    description: "Ngorongoro Crater & Serengeti National Park"
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "9 Days Photographic Safari"
  },
  {
    icon: FaUtensils,
    title: "Meals",
    description: "Full board during safari days"
  }
];

/* ---------------- ITINERARY ---------------- */

const itinerary = [
  {
    dayNum: "01",
    title: "Arrival Kilimanjaro – Arusha",
    image: imageStoneTown,
    body: "Upon arrival at Kilimanjaro International Airport you will be welcomed and transferred to your hotel in Arusha for dinner and overnight.",
    activities: "Arrival, hotel transfer",
    tags: ["Arrival", "Arusha"]
  },
  {
    dayNum: "02",
    title: "Arusha – Cultural Tours – Ngorongoro",
    image: imageHotel,
    body: "Visit Mto wa Mbu village and experience local culture before continuing to the Ngorongoro Highlands for dinner and overnight on the crater rim.",
    activities: "Cultural tours, travel to Ngorongoro",
    tags: ["Culture", "Rift Valley"]
  },
  {
    dayNum: "03",
    title: "Wildlife Photography in Ngorongoro Crater",
    image: imageActivities,
    body: "Descend into the Ngorongoro Crater for spectacular wildlife photography among dense populations of animals.",
    activities: "Crater safari",
    tags: ["Crater", "Photography"]
  }
];

/* ---------------- COMPONENT ---------------- */

export default function PhotographicSafari() {
  const appContext = useContext(AppContext);

  const theme = {
    primary: "#d97706",
    primaryLight: "#f59e0b",
    accent: "#92400e",
    bg: "#fffbeb",
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        fontFamily: "system-ui, sans-serif",
        background: theme.bg,
        color: theme.text
      }}
    >

      {/* HERO */}
      <section
        style={{
          height: "75vh",
          backgroundImage: `linear-gradient(135deg, rgba(23,15,8,0.9), rgba(120,53,15,0.75)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "white",
          padding: "0 2rem"
        }}
      >
        <div style={{ maxWidth: "800px" }}>
          <span
            style={{
              background: "rgba(245,158,11,0.2)",
              border: "1px solid rgba(245,158,11,0.4)",
              padding: "6px 20px",
              borderRadius: "30px",
              fontSize: "0.9rem",
              fontWeight: 600
            }}
          >
            {hero.kicker}
          </span>

          <h1 style={{ fontSize: "3.5rem", marginTop: "1rem" }}>
            {hero.title}
          </h1>

          <p style={{ fontSize: "1.3rem", marginBottom: "2rem" }}>
            {hero.subtitle}
          </p>

          <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span
                key={i}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  padding: "6px 14px",
                  borderRadius: "40px",
                  fontSize: "0.85rem"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>

        {/* OVERVIEW */}
        <section style={{ textAlign: "center", maxWidth: 800, margin: "0 auto 5rem" }}>
          <h2 style={{ fontSize: "2.2rem", marginBottom: "1rem" }}>
            Safari Overview
          </h2>

          <p style={{ lineHeight: 1.8, color: theme.textMuted }}>
            {overview}
          </p>

          {/* QUICK DETAILS */}
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginTop: "2rem",
              flexWrap: "wrap",
              justifyContent: "center"
            }}
          >
            {details.map((d, i) => {
              const Icon = d.icon;

              return (
                <div
                  key={i}
                  style={{
                    background: "white",
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
                  }}
                >
                  <Icon size={20} style={{ color: theme.primary }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>
                      {d.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* HIGHLIGHTS */}
        <section style={{ marginBottom: "5rem" }}>
          <h2 style={{ textAlign: "center", marginBottom: "2rem" }}>
            Safari Highlights
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
              gap: "1.5rem"
            }}
          >
            {highlights.map((h, i) => {
              const Icon = h.icon;

              return (
                <article
                  key={i}
                  style={{
                    background: "white",
                    padding: "1.6rem",
                    borderRadius: "14px",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.06)"
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 10,
                      background: `${h.color}15`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: h.color,
                      fontSize: "1.4rem",
                      marginBottom: "1rem"
                    }}
                  >
                    <Icon />
                  </div>

                  <h3 style={{ fontSize: "1.2rem" }}>{h.title}</h3>
                  <p style={{ color: theme.textMuted }}>{h.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ITINERARY TIMELINE */}
        <section>
          <h2 style={{ textAlign: "center", marginBottom: "3rem" }}>
            Safari Itinerary
          </h2>

          {itinerary.map((day, i) => (
            <article
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1.5rem",
                marginBottom: "2rem",
                background: "white",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)"
              }}
            >
              <div
                style={{
                  minHeight: 260,
                  backgroundImage: `url(${day.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />

              <div style={{ padding: "2rem" }}>
                <h3 style={{ marginBottom: "1rem" }}>
                  Day {day.dayNum} — {day.title}
                </h3>

                <p style={{ color: theme.textMuted, lineHeight: 1.7 }}>
                  {day.body}
                </p>

                <div style={{ marginTop: "1rem" }}>
                  {day.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        marginRight: 6,
                        background: "#fde68a",
                        padding: "4px 10px",
                        borderRadius: 20,
                        fontSize: "0.8rem"
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
                  {day.activities}
                </div>
              </div>
            </article>
          ))}
        </section>

        {/* CTA */}
        <section
          style={{
            textAlign: "center",
            marginTop: "4rem",
            padding: "3rem",
            background: "#fff",
            borderRadius: "20px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.06)"
          }}
        >
          <h3 style={{ marginBottom: "1rem" }}>
            Ready for Your Photographic Safari?
          </h3>

          <button
            style={{
              background: "linear-gradient(135deg,#d97706,#f59e0b)",
              color: "white",
              border: "none",
              padding: "12px 28px",
              borderRadius: 30,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 6
            }}
            onClick={()=>appContext.navTo({ item: 'plan-trip' })}
          >
            Book This Safari <IoMdArrowDropright />
          </button>
        </section>
      </div>
    </div>
  );
}